import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Newspaper } from "lucide-react";
import { api, fullUrl } from "../../lib/api";
import { useLang } from "../../lib/i18n";

export const BlogSection = () => {
    const [posts, setPosts] = useState([]);
    const { t, lang, fs } = useLang();

    useEffect(() => {
        api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
    }, []);

    if (posts.length === 0) return null;

    return (
        <section id="blog" className="relative py-24 sm:py-32" data-testid="blog-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mb-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
                >
                    <div>
                        <p className="font-mono-spec text-xs tracking-[0.3em] text-amber-400 mb-3" dir="ltr">
                            {t("blog_eyebrow")}
                        </p>
                        <h2 className={`font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white ${fs("blog_title")}`}>
                            {t("blog_title")}
                        </h2>
                    </div>
                    <div className="flex flex-col items-start lg:items-end gap-3">
                        <p className={`text-slate-400 text-base max-w-md ${fs("blog_desc")}`}>{t("blog_desc")}</p>
                        <Link
                            to="/blog"
                            data-testid="blog-all-link"
                            className="flex items-center gap-2 text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors duration-300"
                        >
                            <Newspaper className="w-4 h-4" />
                            {t("blog_all")}
                        </Link>
                    </div>
                </motion.div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {posts.map((p, i) => (
                        <motion.article
                            key={p.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            data-testid={`blog-card-${p.slug}`}
                            className="group spotlight-card border border-white/10 rounded-3xl overflow-hidden hover:border-amber-400/40 transition-colors duration-500 flex flex-col"
                        >
                            {p.image && (
                                <div className="relative h-44 overflow-hidden">
                                    <img
                                        src={fullUrl(p.image)}
                                        alt={p.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <span className="absolute bottom-2.5 left-2.5 bg-white/95 rounded-lg px-1.5 py-0.5 shadow-lg" data-testid="post-watermark">
                                        <img src="/assets/brand/mero-logo.jpg" alt="Mero" className="h-5 w-auto object-contain" />
                                    </span>
                                </div>
                            )}
                            <div className="p-6 flex flex-col flex-1">
                                {p.category && (
                                    <span className="self-start text-[10px] font-bold text-amber-400 bg-amber-400/10 border border-amber-400/25 rounded-full px-3 py-1 mb-3">
                                        {p.category}
                                    </span>
                                )}
                                <h3 className="font-display font-bold text-white text-base leading-snug mb-2">
                                    {p.title}
                                </h3>
                                <p className="text-sm text-slate-400 leading-relaxed line-clamp-2 mb-4">
                                    {p.excerpt}
                                </p>
                                <Link
                                    to={`/blog/${p.slug}`}
                                    data-testid={`blog-read-${p.slug}`}
                                    className="mt-auto inline-flex items-center gap-2 text-xs font-extrabold text-amber-400 hover:text-amber-300 transition-colors duration-300"
                                >
                                    {t("blog_read")}
                                    <ArrowLeft className={`w-3.5 h-3.5 ${lang === "en" ? "rotate-180" : ""}`} />
                                </Link>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};
